import { useState, useEffect, useMemo } from 'react';
import { formatVerifiedCoordinates, resolveLocationFromPreset, VerifiedLocation } from '../services/geoService';
import { formatTime } from '../utils/dateUtils';
import { 
  calculateAuthoritativePanchanga,
  AuthoritativePanchanga,
  TithiTransition,
  NakshatraTransition,
  YogaTransition,
  KaranaTransition,
  PitruPakshaContext,
  SankrantiInfo
} from '../utils/panchangaEngine';

export interface PanchangEvaluation {
  date: Date;
  dateString: string;
  monthShort: string;
  dayNumber: number;
  weekdayName: string;
  yearNumber: number;
  fullGregorianDate: string;
  samvatString: string;
  masa: string;
  vikramSamvat: number;
  shakaSamvat: number;
  ayana: 'UTTARAYANA' | 'DAKSHINAYANA';
  ayanaNameEn: string;
  ayanaNameHi: string;
  city: string;
  placeName: string;
  coordinates: { lat: number; lng: number };
  verifiedCoordinatesString: string;
  sunrise: string;
  sunset: string;
  tithi: string;
  tithiNumber: number;
  rawTithiName: string;
  tithiEnd: string;
  paksha: string;
  tithiSegments: { name: string; timeWindow: string; rawName: string }[];
  tithiTransitions?: TithiTransition[];
  isDualTithi: boolean;
  nakshatra: string;
  nakshatraEnd: string;
  nakshatraSegments: { name: string; timeWindow: string }[];
  nakshatraTransitions?: NakshatraTransition[];
  isDualNakshatra: boolean;
  yoga: string;
  yogaEnd: string;
  yogaSegments: { name: string; timeWindow: string }[];
  yogaTransitions?: YogaTransition[];
  isDualYoga: boolean;
  karana: string;
  karanaEnd: string;
  karanaSegments: { name: string; timeWindow: string }[];
  karanaTransitions?: KaranaTransition[];
  isDualKarana: boolean;
  vaara: string;
  abhijitMuhurat: string;
  rahuKaal: string;
  pitruPaksha?: PitruPakshaContext;
  sankranti?: SankrantiInfo;
  // Fasting & Observances
  hasVrat: boolean;
  vratName: string;
  vratNameHi: string;
  vratCategory: string;
  fastingStart: string;
  fastingStartHi: string;
  paranaWindow: string;
  paranaWindowHi: string;
  paranaNotes: string;
  paranaNotesHi: string;
  dailyObservance: string;
  dailyObservanceHi: string;
  upcomingFast: string;
  upcomingFastHi: string;
  upcomingFastDate: string;
  panchangNoteEn: string;
  panchangNoteHi: string;
  // Authoritative Core Backlink
  authoritativeCore?: AuthoritativePanchanga;
}

/**
 * Pure calculation function evaluating Panchang strictly from live date and coordinates
 * via the ONE authoritative Panchanga Calculation Core (panchangaEngine.ts)
 */
export function calculateLivePanchang(
  locationInput?: string | VerifiedLocation,
  calculationDate?: Date
): PanchangEvaluation {
  const today = calculationDate ? new Date(calculationDate) : new Date();

  // Resolve Location
  let loc: VerifiedLocation;
  if (typeof locationInput === 'object' && locationInput !== null && 'latitude' in locationInput) {
    loc = locationInput;
  } else {
    loc = resolveLocationFromPreset(locationInput as string);
  }

  const coords = { lat: loc.latitude, lng: loc.longitude };
  const tzOffsetMinutes = loc.timezoneOffsetMinutes ?? 330;

  // Single Authoritative Panchanga Call
  const authPanchang = calculateAuthoritativePanchanga(today, {
    latitude: coords.lat,
    longitude: coords.lng,
    elevationMeters: 0,
    timezoneOffsetMinutes: tzOffsetMinutes,
    placeName: loc.placeName || loc.name
  });

  const sunriseStr = formatTime(authPanchang.sunrise, false);
  const sunsetStr = formatTime(authPanchang.sunset, false);

  // Tithi Display & Segments
  const tithiSegments = authPanchang.tithiTransitions.map(tr => ({
    name: tr.name,
    rawName: tr.rawName,
    timeWindow: `${tr.formattedStartTime} – ${tr.formattedEndTime}`
  }));
  const isDualTithi = authPanchang.tithiTransitions.length > 1;
  const tithiEndDesc = isDualTithi
    ? `${authPanchang.tithiTransitions[0].rawName} until ${authPanchang.tithiTransitions[0].formattedEndTime}, then ${authPanchang.tithiTransitions[1].rawName}`
    : (authPanchang.tithi.formattedEndTime ? `Active until ${authPanchang.tithi.formattedEndTime}` : 'Active today');

  // Nakshatra Display & Segments
  const nakshatraSegments = authPanchang.nakshatraTransitions.map(tr => ({
    name: tr.name,
    timeWindow: `${tr.formattedStartTime} – ${tr.formattedEndTime}`
  }));
  const isDualNakshatra = authPanchang.nakshatraTransitions.length > 1;
  const nakshatraEndDesc = isDualNakshatra
    ? `${authPanchang.nakshatraTransitions[0].name} until ${authPanchang.nakshatraTransitions[0].formattedEndTime}, then ${authPanchang.nakshatraTransitions[1].name}`
    : (authPanchang.nakshatra.formattedEndTime ? `Active until ${authPanchang.nakshatra.formattedEndTime}` : 'Active today');

  // Yoga Display & Segments
  const yogaSegments = authPanchang.yogaTransitions.map(tr => ({
    name: tr.name,
    timeWindow: `${tr.formattedStartTime} – ${tr.formattedEndTime}`
  }));
  const isDualYoga = authPanchang.yogaTransitions.length > 1;
  const yogaEndDesc = isDualYoga
    ? `${authPanchang.yogaTransitions[0].name} until ${authPanchang.yogaTransitions[0].formattedEndTime}, then ${authPanchang.yogaTransitions[1].name}`
    : (authPanchang.yoga.formattedEndTime ? `Active until ${authPanchang.yoga.formattedEndTime}` : 'Active today');

  // Karana Display & Segments
  const karanaSegments = authPanchang.karanaTransitions.map(tr => ({
    name: tr.name,
    timeWindow: `${tr.formattedStartTime} – ${tr.formattedEndTime}`
  }));
  const isDualKarana = authPanchang.karanaTransitions.length > 1;
  const karanaEndDesc = isDualKarana
    ? `${authPanchang.karanaTransitions[0].name} until ${authPanchang.karanaTransitions[0].formattedEndTime}, then ${authPanchang.karanaTransitions[1].name}`
    : (authPanchang.karana.formattedEndTime ? `Active until ${authPanchang.karana.formattedEndTime}` : 'Active today');

  // Fasting & Observance - Consumed directly from Authoritative Core
  const vo = authPanchang.vratObservance;

  const monthShort = today.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const dayNumber = today.getDate();
  const weekdayName = today.toLocaleDateString('en-US', { weekday: 'long' });
  const yearNumber = today.getFullYear();
  const fullGregorianDate = `${weekdayName}, ${dayNumber} ${today.toLocaleDateString('en-US', { month: 'long' })} ${yearNumber}`;

  const verifiedCoordinatesString = formatVerifiedCoordinates(coords.lat, coords.lng, tzOffsetMinutes);

  return {
    date: today,
    dateString: fullGregorianDate,
    monthShort,
    dayNumber,
    weekdayName,
    yearNumber,
    fullGregorianDate,
    samvatString: authPanchang.samvatString,
    masa: authPanchang.masa,
    vikramSamvat: authPanchang.vikramSamvat,
    shakaSamvat: authPanchang.shakaSamvat,
    ayana: authPanchang.ayana,
    ayanaNameEn: authPanchang.ayanaNameEn,
    ayanaNameHi: authPanchang.ayanaNameHi,
    city: loc.name,
    placeName: loc.placeName,
    coordinates: coords,
    verifiedCoordinatesString,
    sunrise: sunriseStr,
    sunset: sunsetStr,
    tithi: authPanchang.tithi.name,
    tithiNumber: authPanchang.tithi.number,
    rawTithiName: authPanchang.tithi.rawName,
    tithiEnd: tithiEndDesc,
    paksha: authPanchang.tithi.paksha,
    tithiSegments,
    isDualTithi: false,
    nakshatra: authPanchang.nakshatra.name,
    nakshatraEnd: nakshatraEndDesc,
    nakshatraSegments,
    isDualNakshatra,
    yoga: authPanchang.yoga.name,
    yogaEnd: yogaEndDesc,
    yogaSegments,
    isDualYoga,
    karana: authPanchang.karana.name,
    karanaEnd: karanaEndDesc,
    karanaSegments,
    isDualKarana,
    vaara: `${authPanchang.vara.nameSanskrit} (${authPanchang.vara.nameEn})`,
    abhijitMuhurat: `${authPanchang.abhijit.start} – ${authPanchang.abhijit.end}`,
    rahuKaal: `${authPanchang.rahuKalam.start} – ${authPanchang.rahuKalam.end}`,
    hasVrat: vo.hasVrat,
    vratName: vo.vratName,
    vratNameHi: vo.vratNameHi,
    vratCategory: vo.vratCategory,
    fastingStart: vo.fastingStart,
    fastingStartHi: vo.fastingStartHi,
    paranaWindow: vo.paranaWindow,
    paranaWindowHi: vo.paranaWindowHi,
    paranaNotes: vo.paranaNotes,
    paranaNotesHi: vo.paranaNotesHi,
    dailyObservance: vo.dailyObservance,
    dailyObservanceHi: vo.dailyObservanceHi,
    upcomingFast: vo.upcomingFast,
    upcomingFastHi: vo.upcomingFastHi,
    upcomingFastDate: vo.upcomingFastDate,
    panchangNoteEn: vo.panchangNoteEn,
    panchangNoteHi: vo.panchangNoteHi,
    tithiTransitions: authPanchang.tithiTransitions,
    nakshatraTransitions: authPanchang.nakshatraTransitions,
    yogaTransitions: authPanchang.yogaTransitions,
    karanaTransitions: authPanchang.karanaTransitions,
    pitruPaksha: authPanchang.pitruPaksha,
    sankranti: authPanchang.sankranti,
    authoritativeCore: authPanchang
  };
}

export function usePanchang(
  locationInput?: string | VerifiedLocation,
  customDate?: Date
): PanchangEvaluation {
  const [prefVersion, setPrefVersion] = useState(0);

  useEffect(() => {
    const handlePrefChange = () => {
      setPrefVersion((v) => v + 1);
    };
    window.addEventListener('astro_location_pref_changed', handlePrefChange);
    window.addEventListener('astro_time_format_changed', handlePrefChange);
    return () => {
      window.removeEventListener('astro_location_pref_changed', handlePrefChange);
      window.removeEventListener('astro_time_format_changed', handlePrefChange);
    };
  }, []);

  return useMemo(() => {
    return calculateLivePanchang(locationInput, customDate);
  }, [locationInput, customDate, prefVersion]);
}
